import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, CircleDollarSign, Lock, ShieldAlert, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [state, setState] = useState({ loading: true, valid: false, email: "", error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, valid: false, email: "", error: "Geçersiz bağlantı" });
      return;
    }
    api
      .get(`/auth/reset-password/verify/${token}`)
      .then((r) => setState({ loading: false, valid: true, email: r.data.email, error: "" }))
      .catch((err) => setState({
        loading: false,
        valid: false,
        email: "",
        error: formatApiErrorDetail(err.response?.data?.detail) || "Bağlantı doğrulanamadı",
      }));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Parola en az 6 karakter olmalı");
      return;
    }
    if (password !== confirm) {
      setError("Parolalar eşleşmiyor");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Sıfırlama başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell" data-testid="reset-page">
      <aside className="auth-hero">
        <div className="auth-brand">
          <CircleDollarSign size={22} />
          <span>verafin</span>
        </div>
        <h1>Yeni parolanızı belirleyin.</h1>
        <p>En az 6 karakter kullanın, sık kullandığınız başka bir sitedeki parolanızı yeniden kullanmayın.</p>
        <div className="auth-highlights">
          <div className="hl">
            <ShieldCheck size={17} />
            <span>bcrypt ile güvenli saklanır</span>
          </div>
          <div className="hl">
            <Lock size={17} />
            <span>Sıfırlama bağlantısı tek kullanımlık</span>
          </div>
        </div>
      </aside>

      <section className="auth-card">
        <h2>Parola sıfırla</h2>
        {state.loading ? (
          <p className="auth-sub" data-testid="reset-loading">Bağlantı doğrulanıyor…</p>
        ) : !state.valid ? (
          <div className="auth-invalid" data-testid="reset-invalid">
            <div className="auth-invalid-badge">
              <ShieldAlert size={18} />
            </div>
            <h3>Bağlantı geçersiz</h3>
            <p>{state.error}</p>
            <Link to="/forgot-password" className="primary-btn" data-testid="request-new-link">
              Yeni bağlantı iste
            </Link>
          </div>
        ) : done ? (
          <div className="auth-success" data-testid="reset-success">
            <div className="auth-success-badge">
              <CheckCircle2 size={18} />
            </div>
            <h3>Parolanız güncellendi</h3>
            <p>Birkaç saniye içinde giriş sayfasına yönlendirileceksiniz.</p>
          </div>
        ) : (
          <>
            <p className="auth-sub">
              <strong>{state.email}</strong> için yeni parola belirleyin.
            </p>
            <form onSubmit={onSubmit}>
              {error ? (
                <div className="auth-error" data-testid="reset-error">
                  {error}
                </div>
              ) : null}
              <label>
                <span>YENİ PAROLA</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                  autoFocus
                />
              </label>
              <label>
                <span>PAROLA TEKRAR</span>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-confirm-input"
                />
              </label>
              <button type="submit" className="primary-btn" disabled={busy} data-testid="reset-submit-button">
                {busy ? "Kaydediliyor…" : "Parolayı güncelle"}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
